// REBEL PROOF™ — On-chain diploma section for Trendia hero (left column).
// Full-width diploma + description below. Entry animations match the rest of the landing
// (Framer Motion `initial / whileInView / viewport.once` — same pattern as TrendIA cards).
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../i18n/LanguageContext";
import { getDiplomaI18n } from "../../i18n/diploma-landing";

// ───────────────────────── Reusable diploma SVG ─────────────────────────
const DiplomaSVG = ({ d, size = "full", uid = "main" }) => {
  const borderRadius = size === "modal" ? 12 : 10;
  const shadow =
    size === "modal"
      ? "0 0 80px rgba(245,166,35,.3), 0 20px 60px rgba(0,0,0,.8)"
      : "0 0 30px rgba(245,166,35,.15), 0 8px 32px rgba(0,0,0,.6)";

  return (
    <svg
      viewBox="0 0 900 620"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", display: "block", borderRadius, boxShadow: shadow }}
      data-testid={`diploma-svg-${size}`}
    >
      <defs>
        <linearGradient id={`dlBg-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0a0b16" />
          <stop offset="50%" stopColor="#0d0e1c" />
          <stop offset="100%" stopColor="#080910" />
        </linearGradient>
        <linearGradient id={`dlGold-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#8B6914" />
          <stop offset="30%" stopColor="#f5a623" />
          <stop offset="60%" stopColor="#ffd700" />
          <stop offset="100%" stopColor="#C4921A" />
        </linearGradient>
      </defs>

      {/* Background */}
      <rect width="900" height="620" fill={`url(#dlBg-${uid})`} />
      <ellipse cx="450" cy="310" rx="380" ry="260" fill="rgba(245,166,35,.03)" />

      {/* Frames */}
      <rect x="14" y="14" width="872" height="592" fill="none" stroke={`url(#dlGold-${uid})`} strokeWidth="3" />
      <rect x="22" y="22" width="856" height="576" fill="none" stroke="rgba(245,166,35,.25)" strokeWidth="1" />

      {/* Decorative corners with circles */}
      <path d="M14,14 L14,80 M14,14 L80,14" fill="none" stroke={`url(#dlGold-${uid})`} strokeWidth="1.5" />
      <circle cx="45" cy="45" r="8" fill="rgba(245,166,35,.15)" stroke={`url(#dlGold-${uid})`} strokeWidth="1.5" />
      <path d="M886,14 L886,80 M886,14 L820,14" fill="none" stroke={`url(#dlGold-${uid})`} strokeWidth="1.5" />
      <circle cx="855" cy="45" r="8" fill="rgba(245,166,35,.15)" stroke={`url(#dlGold-${uid})`} strokeWidth="1.5" />
      <path d="M14,606 L14,540 M14,606 L80,606" fill="none" stroke={`url(#dlGold-${uid})`} strokeWidth="1.5" />
      <circle cx="45" cy="575" r="8" fill="rgba(245,166,35,.15)" stroke={`url(#dlGold-${uid})`} strokeWidth="1.5" />
      <path d="M886,606 L886,540 M886,606 L820,606" fill="none" stroke={`url(#dlGold-${uid})`} strokeWidth="1.5" />
      <circle cx="855" cy="575" r="8" fill="rgba(245,166,35,.15)" stroke={`url(#dlGold-${uid})`} strokeWidth="1.5" />

      {/* ACADEMIA · RE₿EL · PROOF™ */}
      <text x="450" y="68" textAnchor="middle" fontFamily="'Arial Black',sans-serif" fontSize="11" fill="rgba(245,166,35,.6)" letterSpacing="8">
        {d.academia}
      </text>
      <text x="450" y="122" textAnchor="middle" fontFamily="'Arial Black',sans-serif" fontSize="60" fontWeight="900" letterSpacing="-1">
        <tspan fill={`url(#dlGold-${uid})`}>RE</tspan>
        <tspan fill="#ffd700">₿</tspan>
        <tspan fill={`url(#dlGold-${uid})`}>EL</tspan>
      </text>
      <text x="450" y="148" textAnchor="middle" fontFamily="'Arial Black',sans-serif" fontSize="20" fill="rgba(245,166,35,.5)" letterSpacing="12">
        PROOF™
      </text>

      {/* Divider 1 */}
      <line x1="80" y1="165" x2="370" y2="165" stroke={`url(#dlGold-${uid})`} strokeWidth="1" />
      <circle cx="450" cy="165" r="5" fill={`url(#dlGold-${uid})`} />
      <line x1="530" y1="165" x2="820" y2="165" stroke={`url(#dlGold-${uid})`} strokeWidth="1" />
      <circle cx="370" cy="165" r="2.5" fill="rgba(245,166,35,.4)" />
      <circle cx="530" cy="165" r="2.5" fill="rgba(245,166,35,.4)" />

      {/* Certifica que */}
      <text x="450" y="210" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="13" fill="rgba(255,255,255,.4)" letterSpacing="6">
        {d.certificaQue}
      </text>

      {/* Name */}
      <text x="450" y="265" textAnchor="middle" fontFamily="Georgia,serif" fontSize="42" fontWeight="700" fontStyle="italic" fill="#f0f4ff">
        Peter Stanga
      </text>
      <line x1="180" y1="280" x2="720" y2="280" stroke="rgba(245,166,35,.3)" strokeWidth="1" />

      {/* Description */}
      <text x="450" y="318" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="12" fill="rgba(255,255,255,.5)" letterSpacing="2">
        {d.haCompletado}
      </text>
      <text x="450" y="360" textAnchor="middle" fontFamily="'Arial Black',sans-serif" fontSize="26" fontWeight="900" fill={`url(#dlGold-${uid})`} letterSpacing="1">
        NIVEL 6 · MASTER TRADER
      </text>
      <text x="450" y="392" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="13" fill="rgba(255,255,255,.4)" letterSpacing="2">
        RE₿EL Academy · Trendia
      </text>

      {/* Divider 2 */}
      <line x1="80" y1="418" x2="370" y2="418" stroke={`url(#dlGold-${uid})`} strokeWidth="1" />
      <circle cx="450" cy="418" r="5" fill={`url(#dlGold-${uid})`} />
      <line x1="530" y1="418" x2="820" y2="418" stroke={`url(#dlGold-${uid})`} strokeWidth="1" />

      {/* Seal */}
      <circle cx="450" cy="474" r="55" fill="rgba(245,166,35,.06)" stroke={`url(#dlGold-${uid})`} strokeWidth="2" />
      <circle cx="450" cy="474" r="46" fill="none" stroke="rgba(245,166,35,.3)" strokeWidth="1" strokeDasharray="4,4" />
      <circle cx="450" cy="474" r="38" fill="rgba(245,166,35,.06)" stroke="rgba(245,166,35,.15)" strokeWidth="1" />
      <text x="450" y="484" textAnchor="middle" fontFamily="'Arial Black',sans-serif" fontSize="28" fontWeight="900" fill={`url(#dlGold-${uid})`}>
        ₿
      </text>
      <text x="450" y="508" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="7" fill="rgba(245,166,35,.5)" letterSpacing="3">
        VERIFIED
      </text>

      {/* Left column: date */}
      <text x="165" y="457" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="9" fill="rgba(255,255,255,.3)" letterSpacing="2">
        {d.fechaEmision}
      </text>
      <text x="165" y="476" textAnchor="middle" fontFamily="Georgia,serif" fontSize="14" fill="rgba(255,255,255,.7)">
        {d.fechaValor}
      </text>
      <line x1="100" y1="492" x2="230" y2="492" stroke="rgba(245,166,35,.3)" strokeWidth="1" />
      <text x="165" y="507" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="8" fill="rgba(255,255,255,.25)" letterSpacing="2">
        {d.emisionLabel}
      </text>

      {/* Right column: signature */}
      <text x="735" y="457" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="9" fill="rgba(255,255,255,.3)" letterSpacing="2">
        {d.firmaDigital}
      </text>
      <path
        d="M670,474 C690,467 700,480 715,470 C725,464 730,474 745,467 C755,462 760,474 775,470 C785,467 790,474 800,472"
        fill="none"
        stroke="rgba(245,166,35,.6)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line x1="670" y1="492" x2="800" y2="492" stroke="rgba(245,166,35,.3)" strokeWidth="1" />
      <text x="735" y="507" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="8" fill="rgba(255,255,255,.25)" letterSpacing="2">
        {d.directorAcademico}
      </text>

      {/* Hash block */}
      <rect x="220" y="528" width="460" height="30" rx="4" fill="rgba(0,212,255,.04)" stroke="rgba(0,212,255,.15)" strokeWidth="1" />
      <text x="450" y="540" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="7.5" fill="rgba(0,212,255,.5)" letterSpacing="1">
        ⛓ {d.hashOnChain}
      </text>
      <text x="450" y="552" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="8" fill="rgba(0,212,255,.7)" letterSpacing="1">
        0x4a8f2c9e1b7d3f6a0e5c8b2a9f4d1e7b3c6a0f
      </text>

      {/* Footer */}
      <text x="450" y="584" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="9" fill="rgba(245,166,35,.35)" letterSpacing="4">
        {d.footerVerificado}
      </text>
      <text x="450" y="600" textAnchor="middle" fontFamily="'Courier New',monospace" fontSize="7" fill="rgba(255,255,255,.15)" letterSpacing="3">
        {d.certificadoMeta}
      </text>
    </svg>
  );
};

// ───────────────────────── Hover-aware full-width diploma ─────────────────────────
const DiplomaFull = ({ d, label, onOpen }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="diploma-full"
      style={{
        width: "100%",
        cursor: "pointer",
        position: "relative",
        transition: "transform .3s ease, filter .3s ease",
        transform: hover ? "scale(1.02) translateY(-4px)" : "scale(1) translateY(0)",
        filter: hover ? "drop-shadow(0 0 40px rgba(245,166,35,.35))" : "drop-shadow(0 0 0 transparent)",
      }}
    >
      <DiplomaSVG d={d} size="full" uid="hero" />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: 12,
          right: 12,
          background: "rgba(245,166,35,.15)",
          border: "1px solid rgba(245,166,35,.3)",
          borderRadius: 6,
          padding: "4px 10px",
          fontSize: 10,
          color: "#f5a623",
          fontFamily: "'Courier New',monospace",
          letterSpacing: ".1em",
          pointerEvents: "none",
          opacity: hover ? 1 : 0.7,
          transition: "opacity .2s",
        }}
      >
        🔍 {label}
      </div>
    </div>
  );
};

// ───────────────────────── Main section ─────────────────────────
export const DiplomaSection = () => {
  const { lang } = useLanguage();
  const i = getDiplomaI18n(lang);
  const [modalOpen, setModalOpen] = useState(false);
  const [verifyHover, setVerifyHover] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen]);

  return (
    <>
      <div
        data-testid="diploma-section"
        style={{
          marginTop: 28,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* Badge — entrance animation matches other landing chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          data-testid="diploma-badge"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            background: "rgba(245,166,35,.1)",
            border: "1px solid rgba(245,166,35,.35)",
            borderRadius: 100,
            padding: "4px 14px",
            width: "fit-content",
            fontSize: 11,
            color: "#f5a623",
            letterSpacing: ".12em",
            fontFamily: "'Courier New', monospace",
            fontWeight: 700,
          }}
        >
          {i.badge}
        </motion.div>

        {/* Diploma full width — same motion pattern as TrendIA gallery cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        >
          <DiplomaFull d={i.diploma} label={i.verEjemplo} onOpen={() => setModalOpen(true)} />
        </motion.div>

        {/* Description block — staggered fade-up */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          <h3
            data-testid="diploma-title"
            style={{
              fontSize: "clamp(15px, 2vw, 20px)",
              fontWeight: 700,
              color: "#f0f4ff",
              margin: 0,
              lineHeight: 1.3,
            }}
          >
            {i.titulo}
          </h3>

          <p
            style={{
              fontSize: "clamp(12px, 1.4vw, 14px)",
              color: "rgba(255,255,255,.55)",
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            {i.descripcion}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {i.puntos.map((punto, idx) => (
              <div
                key={idx}
                data-testid={`diploma-bullet-${idx}`}
                style={{
                  fontSize: "clamp(11px, 1.3vw, 13px)",
                  color: "rgba(255,255,255,.6)",
                  lineHeight: 1.4,
                }}
              >
                {punto}
              </div>
            ))}
          </div>

          <a
            href="https://rebelbtc.com/verify"
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setVerifyHover(true)}
            onMouseLeave={() => setVerifyHover(false)}
            data-testid="diploma-verify-link"
            style={{
              width: "fit-content",
              marginTop: 4,
              fontSize: 11,
              color: verifyHover ? "#00d4ff" : "rgba(0,212,255,.7)",
              textDecoration: "none",
              fontFamily: "'Courier New',monospace",
              letterSpacing: ".08em",
              borderBottom: `1px solid ${verifyHover ? "#00d4ff" : "rgba(0,212,255,.25)"}`,
              paddingBottom: 2,
              transition: "color .15s, border-color .15s",
            }}
          >
            🔗 rebelbtc.com/verify →
          </a>
        </motion.div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          onClick={() => setModalOpen(false)}
          data-testid="diploma-modal"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,8,15,.92)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            cursor: "pointer",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 860, cursor: "default" }}
          >
            <DiplomaSVG d={i.diploma} size="modal" uid="modal" />
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button
                onClick={() => setModalOpen(false)}
                data-testid="diploma-modal-close"
                style={{
                  background: "none",
                  border: "1px solid rgba(255,255,255,.15)",
                  color: "rgba(255,255,255,.7)",
                  padding: "7px 20px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 12,
                  letterSpacing: ".06em",
                }}
              >
                {i.cerrar}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default DiplomaSection;
